import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { WorkerInviteStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
} from '../../components/ui';
import { getBrand } from '../../constants/brands';
import { WORKER_ACCOUNTS } from '../../services/serviceService/queries';
import {
  useInviteWorkerMutation,
  useMyWorkerQuery,
  useRemoveWorkerAccountMutation,
  useWorkerAccountsQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { serviceErrorMessage } from './serviceErrors';
import { workerManagePath } from './servicePaths';
import { inviteMessage, inviteStatusView, inviteTargetFrom } from './workerInvite';

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED) : '';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Who signs in as a worker (spec 019 W8, D14b). The Provider invites by email
 * or phone; the invite link is shown once, to copy and send (there is no SMS
 * sender). A removed login loses every job at once.
 */
export default function WorkerAccountsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const workerQuery = useMyWorkerQuery({
    variables: { workerCarId: id },
    fetchPolicy: 'cache-first',
  });
  const accountsQuery = useWorkerAccountsQuery({
    variables: { workerCarId: id },
    fetchPolicy: 'cache-and-network',
  });
  const refetch = [{ query: WORKER_ACCOUNTS, variables: { workerCarId: id } }];
  const [invite, { loading: inviting }] = useInviteWorkerMutation({ refetchQueries: refetch });
  const [removeAccount] = useRemoveWorkerAccountMutation({ refetchQueries: refetch });
  const [contact, setContact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const workerName = workerQuery.data?.myWorker.worker.displayName ?? 'this worker';
  const accounts = accountsQuery.data?.workerAccounts ?? [];

  const onInvite = async () => {
    const { target, error: problem } = inviteTargetFrom(contact);
    if (!target) {
      setError(problem ?? null);
      return;
    }
    setError(null);
    try {
      const { data } = await invite({ variables: { workerCarId: id, ...target } });
      setInviteUrl(data?.inviteWorker.inviteUrl ?? null);
      setContact('');
    } catch (e) {
      setError(serviceErrorMessage(e, 'Could not send the invite'));
    }
  };

  const onRemove = async (accountId: string) => {
    if (!window.confirm(`Remove this login? They lose access to ${workerName}'s jobs right away.`))
      return;
    setRemoving(accountId);
    try {
      await removeAccount({ variables: { workerAccountId: accountId } });
      toast.success('Login removed');
    } catch (e) {
      toast.error(serviceErrorMessage(e, 'Could not remove the login'));
    } finally {
      setRemoving(null);
    }
  };

  const message = inviteUrl ? inviteMessage(inviteUrl, workerName, getBrand().name) : '';

  return (
    <PageContainer width="lg" canvas data-testid="worker-accounts">
      <PageHeader
        eyebrow="Logins"
        title={`Who signs in as ${workerName}`}
        description="A worker sees only their own jobs: the address and door code shortly before, the checklist and photos. Never your prices or earnings."
        backTo={{ to: workerManagePath(id), label: workerName }}
      />
      <Stack $gap={6}>
        <Card>
          <CardHeader
            title="Invite someone"
            description="Enter their email address or mobile number."
          />
          <CardBody>
            <Stack $gap={4}>
              {error && (
                <Alert tone="error" title="Not sent">
                  {error}
                </Alert>
              )}
              <Field label="Email or phone" htmlFor="invite-contact">
                <Input
                  id="invite-contact"
                  value={contact}
                  placeholder="ana@example.com or (602) 555-0100"
                  onChange={event => setContact(event.target.value)}
                  data-testid="invite-contact"
                />
              </Field>
              <div>
                <Button
                  type="button"
                  text="Create invite"
                  onClick={onInvite}
                  isLoading={inviting}
                  dataTestId="invite-submit"
                />
              </div>
              {inviteUrl && (
                <Alert
                  tone="success"
                  title="Send this link now"
                  data-testid="invite-url"
                  actions={
                    <Button
                      type="button"
                      theme="secondary"
                      size="sm"
                      text="Copy"
                      onClick={async () => {
                        if (await copyText(message)) toast.success('Copied');
                        else toast.error('Copy failed: select the text instead');
                      }}
                      dataTestId="invite-copy"
                    />
                  }
                >
                  <Stack $gap={4}>
                    <span>
                      We do not text the invite. Copy the message and send it yourself. It is shown
                      only once.
                    </span>
                    <code style={{ wordBreak: 'break-all' }}>{message}</code>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Logins" />
          {accountsQuery.loading && !accountsQuery.data ? (
            <CardBody>
              <LoadingBlock label="Loading logins" />
            </CardBody>
          ) : accounts.length === 0 ? (
            <EmptyState
              icon={faUserPlus}
              compact
              title="No logins yet"
              description="Invite the person who does this worker's jobs."
            />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>Person</TH>
                    <TH>Status</TH>
                    <TH>Invited</TH>
                    <TH $align="right">Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {accounts.map(account => {
                    const view = inviteStatusView(account);
                    return (
                      <TR key={account.id} data-testid={`worker-account-${account.id}`}>
                        <TD>
                          <div>
                            {account.userName ||
                              account.inviteEmail ||
                              account.invitePhone ||
                              'Unknown'}
                          </div>
                          {account.userName && (account.inviteEmail || account.invitePhone) && (
                            <Muted $size="xs">{account.inviteEmail || account.invitePhone}</Muted>
                          )}
                        </TD>
                        <TD>
                          <Badge tone={view.tone}>{view.label}</Badge>
                        </TD>
                        <TD>
                          <Muted $size="sm">
                            {account.acceptedAt
                              ? `Joined ${formatDate(account.acceptedAt)}`
                              : formatDate(account.invitedAt)}
                          </Muted>
                        </TD>
                        <TDActions>
                          {account.status !== WorkerInviteStatus.Removed && (
                            <InlineRow>
                              <Button
                                type="button"
                                theme="danger"
                                size="sm"
                                text="Remove"
                                isLoading={removing === account.id}
                                onClick={() => onRemove(account.id)}
                              />
                            </InlineRow>
                          )}
                        </TDActions>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
